import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopHeadding from "../../components/layout/TopHeadding";
import CompanyListTable from "../../components/tables/CompanyListTable";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { fetchAllCompanies, exportCompaniesCSV, fetchAllClients } from "../../store/slices/userSlice";
import plus_icon from "../../assets/images/plus_icon.svg";
import plus_icon_blue from "../../assets/images/plus_icon_blue.svg";
import export_icon from "../../assets/images/export_icon.svg";
import SelectCommonModal from "../../components/popups/SelectCommonModal";
import FilterTopBar from "../../components/navigation/FilterTopBar";

const Companies = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // 1. PAGE-SPECIFIC KEYS
  const COMPANY_PAGE_KEY = "mainCompanyListing";
  const MODAL_CLIENT_KEY = "modalClientList";
  const POPUP_COMPANY_KEY = "popupCompanyList";

  // 2. SELECTORS (Naye key-based structure ke mutabiq)
  const { user, role, exportCSVLoading } = useAppSelector((s) => s.user);

  const companyList = useAppSelector((s) => s.user.listDataByKey[COMPANY_PAGE_KEY] || []);
  const loadingCompanies = useAppSelector((s) => s.user.listLoadingByKey[COMPANY_PAGE_KEY] || false);
  const totalRecords = useAppSelector((s) => s.user.totalByKey[COMPANY_PAGE_KEY] || 0);

  const clientListForModal = useAppSelector((s) => s.user.listDataByKey[MODAL_CLIENT_KEY] || []);
  const popupCompanyList = useAppSelector((s) => s.user.listDataByKey[POPUP_COMPANY_KEY] || []);

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [companyShowModal, setCompanyShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    payRange: [0, 1000000],
    status: null
  });

  const prevSearchRef = useRef(search);
  const prevFiltersRef = useRef(filters);

  const buildCompanyParams = (extra = {}) => {
    const params = {
      startDate: filters.startDate ?? null,
      endDate: filters.endDate ?? null,
      status: filters.status,
      minPayAvg: filters.payRange[0],
      maxPayAvg: filters.payRange[1],
      ...extra,
    };

    if (search) {
      params.search = search;
    }

    if (role === "Client") {
      params.clientId = user.id;
    }

    return params;
  };

  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  useEffect(() => {
    const maxPage = Math.ceil(totalRecords / pageSize) || 1;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalRecords, page, pageSize]);

  useEffect(() => {
    if (!user?.id || !role) return;

    const searchChanged = prevSearchRef.current !== search;
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    const debounceDelay = (searchChanged || filtersChanged) ? 400 : 0;

    const timeout = setTimeout(() => {
      const params = buildCompanyParams({
        pageIndex: page - 1,
        pageSize: pageSize,
      });
      dispatch(fetchAllCompanies({ params, key: COMPANY_PAGE_KEY }));
      prevSearchRef.current = search;
      prevFiltersRef.current = filters;
    }, debounceDelay);

    return () => clearTimeout(timeout);
  }, [user, role, search, dispatch, page, filters]);

  useEffect(() => {
    if (!showModal) return;
    dispatch(fetchAllClients({ key: MODAL_CLIENT_KEY }));
  }, [showModal, dispatch]);

  useEffect(() => {
    if (!companyShowModal) return;
    const params = {
      pageIndex: 0,
      pageSize: 1000, // Fetch all companies for popup
    };
    if (role === "Client") {
      params.clientId = user.id;
    }
    dispatch(fetchAllCompanies({ params, key: POPUP_COMPANY_KEY }));
  }, [companyShowModal, dispatch, role, user?.id]);

  const handleExport = () => {
    const params = buildCompanyParams();
    dispatch(exportCompaniesCSV(params));
  };

  const showExport = role === "Client" ? true : false;

  return (
    <div className="CompaniesPage">
      <TopHeadding
        title="Company List"
        spanText={`(${totalRecords})`}
        showDatepicker={false}
        buttons={[
          ...(role === "SuperAdmin" ? [
            {
              text: exportCSVLoading ? "Exporting" : "Export",
              className: "btn_secondary",
              icon: exportCSVLoading ? null : export_icon,
              disabled: exportCSVLoading,
              onClick: () => {
                if (!exportCSVLoading) {
                  const params = buildCompanyParams();
                  dispatch(exportCompaniesCSV(params));
                }
              },
            }] : []),
          {
            text: "Add Company",
            className: role == "SuperAdmin" ? "btn_primary" : "btn_outline_primary",
            icon: role == "SuperAdmin" ? plus_icon : plus_icon_blue,
            onClick: () => {
              if (role === "SuperAdmin")
                setShowModal(true);
              else
                navigate("/addcompany", { state: { id: user.id, from: "/companies" } });
            },
          },
          ...(role === "Client"
            ? [
              {
                text: "Payroll",
                className: "btn_primary",
                onClick: () => setCompanyShowModal(true),
              },
            ]
            : []),
        ]}
      />

      {companyShowModal && (
        <SelectCommonModal
          title="Please Select Company"
          data={popupCompanyList}
          labelKey="companyName"
          valueKey="id"
          imageKey="logo"
          isMultiple
          onClose={() => setCompanyShowModal(false)}
          onApply={(companies) =>
            navigate("/selectedpayroll", {
              state: {
                clientId: user.id,
                companies
              },
            })
          }
        />
      )}

      <div className="company_table mt_30">
        <div className="container-fluid">
          <div className="white_box py_20 px_20">

            <FilterTopBar
              search={search}
              onSearchChange={setSearch}
              placeholder="Search Company"
              showExport={showExport}
              customExportHandler={handleExport}
              customExportLoading={exportCSVLoading}
              onFilterChange={setFilters}
              filterClassName="ms-auto"
            />

            <CompanyListTable
              className="custom_table_list"
              data={companyList}
              loading={loadingCompanies}
              currentPage={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={(pageNumber) => setPage(pageNumber)}
              pageKey={COMPANY_PAGE_KEY} />

            {showModal && (
              <SelectCommonModal
                title="Please Select Client"
                data={clientListForModal}
                labelKey="name"
                valueKey="id"
                imageKey="profile"
                onClose={() => setShowModal(false)}
                onApply={(client) => navigate("/addcompany", { state: { id: client.id, from: "/companies" } })}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Companies;
